import React, { useState } from 'react';
import { ArrowRight } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import FormPrivacyNotice from '../FormPrivacyNotice';
import {
  submitBookingEnquiry,
  nightsBetween,
  todayISO,
} from '@/lib/submitBookingEnquiry';

const BookingEnquiryDialog = ({ open, onOpenChange }) => {
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');

  const today = todayISO();
  const nights = nightsBetween(checkIn, checkOut);

  const handleCheckInChange = (e) => {
    const value = e.target.value;
    setCheckIn(value);
    if (checkOut && value && checkOut < value) setCheckOut(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    setSubmitting(true);
    const result = await submitBookingEnquiry({
      name: data.name,
      phone: data.phone,
      petName: data.petName,
      petBreed: data.petBreed,
      checkIn,
      checkOut,
      consent,
    });
    setSubmitting(false);
    if (result?.success) {
      form.reset();
      setCheckIn('');
      setCheckOut('');
      setConsent(false);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[95vw] max-w-md overflow-y-auto rounded-2xl p-6">
        <div>
          <DialogTitle className="text-left text-xl font-semibold text-neutral-900">
            Booking enquiry
          </DialogTitle>
          <DialogDescription className="mt-1 text-left text-sm text-neutral-600">
            Share a few details and we'll call you to confirm availability at PetCart Nest.
          </DialogDescription>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="enquiry-name" className="text-xs font-semibold">Your name</Label>
            <Input id="enquiry-name" name="name" required maxLength={120} autoComplete="name" placeholder="Your name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="enquiry-phone" className="text-xs font-semibold">Phone (WhatsApp)</Label>
            <Input
              id="enquiry-phone"
              name="phone"
              type="tel"
              required
              autoComplete="tel"
              inputMode="tel"
              placeholder="+91 98765 43210"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="enquiry-pet-name" className="text-xs font-semibold">Pet name</Label>
              <Input id="enquiry-pet-name" name="petName" required maxLength={80} placeholder="Bruno" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="enquiry-pet-breed" className="text-xs font-semibold">Breed</Label>
              <Input id="enquiry-pet-breed" name="petBreed" required maxLength={80} placeholder="Labrador" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="enquiry-check-in" className="text-xs font-semibold">Check-in</Label>
              <Input
                id="enquiry-check-in"
                type="date"
                required
                min={today}
                value={checkIn}
                onChange={handleCheckInChange}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="enquiry-check-out" className="text-xs font-semibold">Check-out</Label>
              <Input
                id="enquiry-check-out"
                type="date"
                required
                min={checkIn || today}
                value={checkOut}
                onChange={(e) => setCheckOut(e.target.value)}
              />
            </div>
          </div>
          {nights !== null && nights >= 0 && (
            <p className="-mt-2 text-xs font-medium text-neutral-500">
              {nights === 0 ? 'Same-day stay' : `${nights} night${nights === 1 ? '' : 's'}`}
            </p>
          )}
          <FormPrivacyNotice
            checked={consent}
            onChange={setConsent}
            id="booking-enquiry-privacy-consent"
          />
          <Button
            type="submit"
            disabled={submitting}
            className="h-10 rounded-md bg-primary text-sm font-semibold text-white shadow-none hover:bg-primary/90 hover:shadow-none disabled:opacity-60"
          >
            {submitting ? 'Sending…' : 'Send Enquiry'}
            <ArrowRight size={14} weight="bold" />
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BookingEnquiryDialog;
